/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import PropTypes from 'prop-types';
import gnoment from 'ui-cart/shared-components/utils/gnoment';
import { FlexRow, FlexCol, useFlags } from 'sarsaparilla';
import * as types from '../constants';
import { formatMoney } from '../utils/cart_utils';
import { makeFormattedDatesString } from '../utils/date_utils';

function ReservationTotal({ reservation }) {
    const { ticketAdjustMultiDayTicket } = useFlags();
    // Construct the date string
    const dateString = makeFormattedDatesString(reservation, ticketAdjustMultiDayTicket);

    let description = reservation.order_details.description;
    let qty = reservation.quantity;
    let unit = '';
    let recurringReservations = '';
    const total = reservation.total;
    let campingUnits = reservation.order_details.camping_type_of_use;
    campingUnits = campingUnits === 'Overnight' ? 'Night' : campingUnits;
    const isVenueReservation =
        reservation.inventory_type === types.INVENTORY_VENUE_RESERVATIONS ||
        reservation.inventory_type === types.INVENTORY_VENUE_APPLICATION;
    switch (reservation.inventory_type) {
        case types.INVENTORY_VEHICLE_PERMIT: {
            qty = 1;
            let reservationTimeSlot = null;
            const { start_time, end_time } = reservation.order_details;
            if (start_time && end_time) {
                reservationTimeSlot = (
                    <div>
                        {start_time} - {end_time}
                    </div>
                );
            }

            return (
                <FlexRow>
                    <FlexCol xs={8} className="cart-label">
                        <div className="cart-description" translate="no">
                            {description}
                        </div>
                        <div className="cart-quantity">{`Quantity: ${qty} ${unit}`}</div>
                        <div className="cart-date">
                            {gnoment
                                .utc(reservation.order_details.entry_date)
                                .format('MMMM DD, YYYY')}
                        </div>

                        <div className="cart-time">{reservationTimeSlot}</div>
                    </FlexCol>
                    <FlexCol className="cart-value">{formatMoney(total)}</FlexCol>
                </FlexRow>
            );
        }
        case types.INVENTORY_CAMPING:
            qty = reservation.order_details.nights;
            unit = parseInt(qty, 10) === 1 ? `${campingUnits}` : `${campingUnits}s`;
            break;
        case types.INVENTORY_PERMIT:
            unit = 'Permit';
            qty = 1;
            break;
        case types.INVENTORY_TIMED_ENTRY:
        case types.INVENTORY_TICKET:
            if (
                reservation?.order_details?.ticket_quantity ||
                reservation?.order_details?.new_ticket_quantity
            ) {
                qty =
                    parseInt(reservation?.order_details?.new_ticket_quantity, 10) -
                    parseInt(reservation?.order_details?.ticket_quantity, 10);
                if (qty < 1) {
                    qty = reservation?.order_details?.new_ticket_quantity;
                }
            }

            unit = qty === 1 ? 'Ticket' : 'Tickets';
            break;
        case types.INVENTORY_LOTTERY_TICKET:
            qty = 1;
            description = 'Lottery Application';
            break;
        case types.INVENTORY_LOTTERY_PERMIT:
            qty = 1;
            unit = 'Lottery';
            break;
        case types.INVENTORY_ACTIVITYPASS:
            unit = '';
            break;
        case types.INVENTORY_PARKPASS:
            qty = reservation?.order_details?.quantity; // NOTE: this will technically always be 1
            unit = parseInt(qty, 10) === 1 ? 'Pass' : 'Passes';
            break;
        case types.INVENTORY_PASS:
        case types.INVENTORY_INTERAGENCYPASS:
            unit = qty === 1 ? 'Pass' : 'Passes';
            break;
        case types.INVENTORY_TREE_PERMIT:
            unit = qty === 1 ? 'Tree' : 'Trees';
            break;
        case types.INVENTORY_VENUE_RESERVATIONS:
        case types.INVENTORY_VENUE_APPLICATION:
            unit = gnoment.utc(reservation?.order_details?.event_date);
            description = reservation?.order_details?.venue_name;
            if (reservation?.order_details?.recurrence_id) {
                const reservationsCount = reservation?.order_details?.dates?.length;
                recurringReservations = `Reservations x ${reservationsCount || 1}`;
            }
            break;
        case types.INVENTORY_RENTAL:
            qty = reservation.order_details.rental_quantity;
            unit = parseInt(qty, 10) === 1 ? 'Item' : 'Items';
            break;
        case types.INVENTORY_DONATION:
            qty = 1;
            description = `Donation to ${reservation.order_details.asset_name}`;
            break;
        case types.INVENTORY_GIFT_CARD:
            return (
                <FlexRow>
                    <FlexCol xs={8} className="cart-label">
                        <div className="cart-description" translate="no">
                            {description}
                        </div>
                        <div className="cart-quantity" aria-hidden="true">
                            Amount: {formatMoney(total)}
                        </div>
                    </FlexCol>
                    <FlexCol className="cart-value">{formatMoney(total)}</FlexCol>
                </FlexRow>
            );
        default:
            break;
    }

    return (
        <FlexRow>
            <FlexCol xs={8} className="cart-label">
                <div className="cart-description" translate="no">
                    {description}
                </div>
                {!isVenueReservation && (
                    <div className="cart-quantity">{`Quantity: ${qty} ${unit}`}</div>
                )}
                {isVenueReservation && (
                    <>
                        <div className="cart-quantity">
                            {reservation?.order_details?.description} -{' '}
                            {reservation?.order_details?.facility_name}
                        </div>
                        <div className="cart-quantity">
                            {reservation?.order_details?.recurrence_id &&
                            reservation?.order_details?.dates
                                ? reservation?.order_details?.dates
                                      ?.sort(
                                          (a, b) =>
                                              gnoment(a).valueOf() - gnoment(b).valueOf()
                                      )
                                      .map((item) => gnoment.utc(item).format('MM/DD/YY'))
                                      .join(', ')
                                : gnoment.utc(unit).format('ddd, ll')}
                            <div>{recurringReservations}</div>
                        </div>
                    </>
                )}
                <div className="cart-dates">{dateString}</div>
            </FlexCol>
            <FlexCol className="cart-value">{formatMoney(total)}</FlexCol>
        </FlexRow>
    );
}

ReservationTotal.propTypes = {
    reservation: PropTypes.object.isRequired,
};

export default ReservationTotal;

// cSpell:ignore ACTIVITYPASS, PARKPASS
